// Beginscherm als widgets (golf 6b, builder U; verzoek oprichter 2026-09-26: "Ik wil het
// beginscherm kunnen herschikken als zijnde widgets, ik wil daar de gebedstijden op kunnen
// toevoegen"). Puur en zonder Zod (ook te importeren via `@amumin/domain/widgets`): welke widgets
// er zijn, bij welke module ze horen, de standaardindeling per gezin en het opschonen van een
// opgeslagen indeling. De keuze per persoon staat in `ui_preference` (0042_widgets); zonder keuze
// geldt de standaard hieronder, die gelijk is aan de oude vaste pagina Vandaag.
import { cleanHomeCards } from './homeGrid.js';
export * from './homeGrid.js';
export const WIDGET_IDS = [
    'now',
    'agenda',
    'tasks',
    'tomorrow',
    'weather',
    'rides',
    'prayer',
    'goals',
    'shopping',
    'messages',
    'wishes',
    'rotation',
    // Golf 8 (0049_samen_wonen): Wie eet mee, Reserveren en het eigen saldo (Kosten delen).
    'dinner',
    'bookings',
    'balance',
    // Golf 8 (0050): de dagelijkse check-in (Ouderen); een meekijker ziet alleen deze.
    'check_in',
    // 0069: Geldzaken (alleen in je eigen omgeving; alleen jij ziet het).
    'money',
    // To-do's (0058; oprichter 6 okt: "ik kan die widget niet toevoegen bij vandaag"): jouw open
    // to-do's, en in je eigen omgeving ook wat het gezin aan jou toewees (alleen lezen).
    'todos',
    // 0076: schoolplanner (huiswerk van vandaag, toetsen die eraan komen, cijfers om in te voeren).
    'school',
    // 0075: Huisronde (module Eten): de volgende ronde, te laat of uitgesteld, en een knop om te starten.
    'house_round',
];
/** Twee kolommen op een breed scherm; op de telefoon eerst `main`, dan `side` (één kolom). */
export const WIDGET_COLUMNS = ['main', 'side'];
export const WIDGETS = [
    // Beurtrooster (fixer golf 6b r1; huisgenoten): jouw beurt deze week en wie vandaag kookt,
    // bovenaan. Alleen met de module Beurtrooster (standaard alleen aan bij huisgenoten).
    // Golf 8: 'Alles goed' van vandaag, bovenaan (alleen met de module Dagelijkse check-in).
    { id: 'check_in', module: 'check_in', parentOnly: false, defaultColumn: 'main' },
    { id: 'rotation', module: 'chore_rotation', parentOnly: false, defaultColumn: 'main' },
    // Golf 8 (§2.2.4): vanavond eten met de eigen knop; een widget van een slapende module (nog niet
    // ingericht) rendert niets.
    { id: 'dinner', module: 'dinner_list', parentOnly: false, defaultColumn: 'main' },
    { id: 'now', module: null, parentOnly: false, defaultColumn: 'main' },
    { id: 'rides', module: 'rides', parentOnly: false, defaultColumn: 'main' },
    { id: 'weather', module: 'location_weather', parentOnly: true, defaultColumn: 'main' },
    // Gebedstijden: pas zichtbaar als het gezin ze koos (het widget toont niets zonder tijden).
    { id: 'prayer', module: 'prayer', parentOnly: true, defaultColumn: 'main' },
    { id: 'agenda', module: null, parentOnly: false, defaultColumn: 'main' },
    { id: 'tasks', module: null, parentOnly: false, defaultColumn: 'side' },
    // Doelen komen uit Beloningen (0043: een eigen module, alleen bij gezinnen standaard aan).
    { id: 'goals', module: 'rewards', parentOnly: false, defaultColumn: 'side' },
    { id: 'tomorrow', module: null, parentOnly: false, defaultColumn: 'side' },
    // Golf 8: de eigen volgende reservering en per ding vrij/bezet; alleen het EIGEN saldo (nooit
    // dat van een ander op Vandaag; de tab Saldo toont wel ieders saldo).
    { id: 'bookings', module: 'bookings', parentOnly: false, defaultColumn: 'side' },
    { id: 'balance', module: 'expenses', parentOnly: true, defaultColumn: 'side' },
    { id: 'shopping', module: 'food', parentOnly: false, defaultColumn: null },
    { id: 'messages', module: 'messages', parentOnly: false, defaultColumn: null },
    { id: 'wishes', module: 'wishes', parentOnly: false, defaultColumn: null },
    // 0069: wat er deze maand overbleef en de buffer; alleen via Toevoegen (privé).
    { id: 'money', module: 'money', parentOnly: true, defaultColumn: null },
    // To-do's: alleen via Toevoegen (staat niet in de standaardindeling; niets verschuift).
    { id: 'todos', module: 'todos', parentOnly: false, defaultColumn: null },
    // 0076: alleen via Toevoegen (de module School staat standaard uit; niets verschuift). Een kind
    // met een eigen login ziet alleen zijn eigen huiswerk en toetsen (de API filtert).
    { id: 'school', module: 'school', parentOnly: false, defaultColumn: null },
    // 0075: Huisronde; alleen via Toevoegen (niets verschuift in een bestaande indeling).
    { id: 'house_round', module: 'food', parentOnly: false, defaultColumn: null },
];
const BY_ID = new Map(WIDGETS.map((w) => [w.id, w]));
/** Hoogstens zoveel widgets in één indeling (alle ids, één keer). */
export const MAX_WIDGETS = WIDGET_IDS.length;
export function isWidgetId(value) {
    return typeof value === 'string' && BY_ID.has(value);
}
export function widgetDef(id) {
    const def = BY_ID.get(id);
    if (!def)
        throw new TypeError(`onbekend widget ${id}`);
    return def;
}
/** Kan deze persoon dit widget nu gebruiken (module aan, rol, extra voorwaarde)? */
export function widgetAvailable(id, ctx) {
    const def = BY_ID.get(id);
    if (!def)
        return false;
    if (def.parentOnly && ctx.role === 'child')
        return false;
    // Golf 8: een meekijker ziet alleen de check-in (de rest van het huis is niet van hem).
    if (ctx.role === 'relative' && id !== 'check_in')
        return false;
    if (def.module && !ctx.moduleOn(def.module))
        return false;
    const extra = ctx.extra ? ctx.extra[id] : undefined;
    return extra !== false;
}
/** De standaardindeling voor dit gezin en deze persoon (gelijk aan de oude pagina Vandaag). */
export function defaultLayout(ctx) {
    const out = { main: [], side: [] };
    for (const def of WIDGETS) {
        if (def.defaultColumn && widgetAvailable(def.id, ctx))
            out[def.defaultColumn].push(def.id);
    }
    return out;
}
/**
 * Een opgeslagen indeling opschonen: onbekende ids weg, dubbele weg (de eerste telt), niet
 * beschikbare weg (module uit, geen ouder). Zonder opgeslagen indeling: de standaard. Een lege
 * indeling blijft leeg (iemand haalde alles weg; "Standaard" zet hem terug).
 */
export function resolveLayout(stored, ctx) {
    const raw = parseLayout(stored);
    if (!raw)
        return defaultLayout(ctx);
    const seen = new Set();
    const clean = (list) => {
        const out = [];
        for (const id of list) {
            if (seen.has(id))
                continue;
            seen.add(id);
            if (widgetAvailable(id, ctx))
                out.push(id);
        }
        return out;
    };
    const out = { main: clean(raw.main), side: clean(raw.side) };
    if (raw.cards)
        out.cards = raw.cards;
    return out;
}
/** Vorm van een opgeslagen indeling (jsonb; nooit blind vertrouwen). null = geen of kapot. */
export function parseLayout(value) {
    if (typeof value !== 'object' || value === null)
        return null;
    const v = value;
    if (!Array.isArray(v.main) || !Array.isArray(v.side))
        return null;
    const pick = (list) => list.filter(isWidgetId);
    const out = { main: pick(v.main), side: pick(v.side) };
    const cards = cleanHomeCards(v.cards, isWidgetId);
    if (cards)
        out.cards = cards;
    return out;
}
/**
 * Wat bewaard wordt na een wijziging in Aanpassen (fixer golf 6b r2): de nieuwe zichtbare indeling,
 * plus de widgets die nu alleen niet beschikbaar zijn (module even uit, gebedstijden even niet te
 * laden) op hun oude plek ten opzichte van de rest. Zo komt Doelen terug als Beloningen weer aan
 * gaat ("een module uit = zijn widget weg, zonder te wissen"). Weghalen kan alleen wat zichtbaar is,
 * dus een verborgen widget is nooit bewust weggehaald. Zonder opgeslagen indeling is er niets te
 * bewaren: dan geldt wat er te zien is.
 */
export function keepHiddenWidgets(stored, next, ctx) {
    const base = parseLayout(stored);
    if (!base)
        return next;
    const out = { main: next.main.slice(), side: next.side.slice() };
    if (next.cards)
        out.cards = next.cards.slice();
    const inNext = new Set([...next.main, ...next.side]);
    for (const column of WIDGET_COLUMNS) {
        const list = out[column];
        let prev = null;
        for (const id of base[column]) {
            if (!inNext.has(id) && !widgetAvailable(id, ctx)) {
                // Direct na zijn voorganger uit de oude indeling (als die nog in deze kolom staat).
                const at = prev === null ? 0 : list.indexOf(prev) + 1;
                list.splice(at, 0, id);
                inNext.add(id);
                prev = id;
                // Feat/vandaag-raster: ook zijn plek in het raster (als die nog vrij is).
                const own = base.cards ? base.cards.find((c) => c.kind === id) : undefined;
                if (out.cards && own && !out.cards.some((c) => overlaps(c, own)))
                    out.cards.push(own);
            }
            else if (list.indexOf(id) >= 0) {
                prev = id;
            }
        }
    }
    return out;
}
function overlaps(a, b) {
    return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}
/**
 * Een bewuste wissel van soort huishouden zet modules aan (bv. Beurtrooster bij huisgenoten): hun
 * standaardwidgets komen dan ook in een EIGEN indeling, op hun standaardplek (fixer golf 6b r2).
 * null = niets te doen (geen eigen indeling, of alles staat er al in, ook verborgen).
 */
export function addDefaultWidgets(stored, ids) {
    const raw = parseLayout(stored);
    if (!raw)
        return null;
    const out = { main: raw.main.slice(), side: raw.side.slice() };
    // Het raster blijft; een nieuwe kaart krijgt bij het tonen de eerste vrije plek.
    if (raw.cards)
        out.cards = raw.cards.slice();
    let changed = false;
    for (const id of ids) {
        const def = BY_ID.get(id);
        if (!def || !def.defaultColumn || positionOf(out, id))
            continue;
        const column = def.defaultColumn;
        const list = out[column];
        // Voor het eerste widget dat in de standaardvolgorde na dit widget komt; anders onderaan.
        const order = WIDGETS.filter((w) => w.defaultColumn === column).map((w) => w.id);
        const after = order.slice(order.indexOf(id) + 1);
        const at = list.findIndex((x) => after.indexOf(x) >= 0);
        list.splice(at < 0 ? list.length : at, 0, id);
        changed = true;
    }
    return changed ? out : null;
}
/** Widgets die deze persoon nog kan toevoegen (beschikbaar en nog niet in de indeling). */
export function addableWidgets(layout, ctx) {
    const used = new Set([...layout.main, ...layout.side]);
    return WIDGET_IDS.filter((id) => !used.has(id) && widgetAvailable(id, ctx));
}
/** Zelfde indeling (volgorde telt)? */
export function sameLayout(a, b) {
    return a.main.join(',') === b.main.join(',') && a.side.join(',') === b.side.join(',');
}
// ---- Bewerken (Aanpassen-modus; puur, zodat de knoppen en het slepen hetzelfde doen) ---------
/** Plek van een widget: kolom en index; null als het er niet in staat. */
export function positionOf(layout, id) {
    for (const column of WIDGET_COLUMNS) {
        const index = layout[column].indexOf(id);
        if (index >= 0)
            return { column, index };
    }
    return null;
}
/** Eén plek op of neer in de leesvolgorde (main, dan side): zo werkt het ook op de telefoon. */
export function moveWidget(layout, id, dir) {
    const pos = positionOf(layout, id);
    if (!pos)
        return layout;
    const main = layout.main.slice();
    const side = layout.side.slice();
    const list = pos.column === 'main' ? main : side;
    const target = pos.index + dir;
    if (target >= 0 && target < list.length) {
        list.splice(pos.index, 1);
        list.splice(target, 0, id);
        return { main, side };
    }
    // Over de rand van de kolom: naar het eind van main of het begin van side.
    if (pos.column === 'main' && dir === 1) {
        main.splice(pos.index, 1);
        side.unshift(id);
        return { main, side };
    }
    if (pos.column === 'side' && dir === -1) {
        side.splice(pos.index, 1);
        main.push(id);
        return { main, side };
    }
    return layout;
}
/** Naar een plek (slepen): uit de oude kolom halen en in `column` op `index` zetten. */
export function placeWidget(layout, id, column, index) {
    const main = layout.main.filter((x) => x !== id);
    const side = layout.side.filter((x) => x !== id);
    const list = column === 'main' ? main : side;
    const at = Math.max(0, Math.min(list.length, index));
    list.splice(at, 0, id);
    return { main, side };
}
/** Naar de andere kolom (brede weergave), bovenaan. */
export function switchColumn(layout, id) {
    const pos = positionOf(layout, id);
    if (!pos)
        return layout;
    return placeWidget(layout, id, pos.column === 'main' ? 'side' : 'main', 0);
}
export function removeWidget(layout, id) {
    return { main: layout.main.filter((x) => x !== id), side: layout.side.filter((x) => x !== id) };
}
/** Toevoegen: in zijn eigen standaardkolom (of main), onderaan. */
export function addWidget(layout, id) {
    if (positionOf(layout, id))
        return layout;
    const column = BY_ID.get(id)?.defaultColumn ?? 'main';
    return placeWidget(layout, id, column, layout[column].length);
}
// ---- Agendaweergave (onthouden per persoon) ----------------------------------------------------
export const AGENDA_VIEWS = ['day', 'workweek', 'week', 'month'];
export function isAgendaView(value) {
    return typeof value === 'string' && AGENDA_VIEWS.indexOf(value) >= 0;
}
//# sourceMappingURL=index.js.map